import React, { useEffect, useState } from "react";
import { Clock, Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function OracleHistory({ currentSessionId, onLoadSession, onNewChat, refreshKey = 0 }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    if (!user) { setSessions([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/oracle/sessions");
      setSessions(Array.isArray(data) ? data : []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.user_id, currentSessionId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = async (s) => {
    try {
      const { data } = await api.get(`/oracle/sessions/${s.id}`);
      onLoadSession?.(s.id, data.messages || [], data.title || s.title || "");
    } catch {
      toast.error("Could not load that conversation.");
    }
  };

  const remove = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      await api.delete(`/oracle/sessions/${s.id}`);
      setSessions((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Conversation deleted.");
      if (s.id === currentSessionId) onNewChat?.();
    } catch { toast.error("Could not delete."); }
  };

  const startRename = (e, s) => {
    e.stopPropagation();
    setEditingId(s.id);
    setEditText(s.title || "");
  };

  const saveRename = async (e, s) => {
    e.stopPropagation();
    const title = editText.trim();
    if (!title) { setEditingId(null); return; }
    try {
      await api.patch(`/oracle/sessions/${s.id}`, { title });
      setSessions((arr) => arr.map((x) => (x.id === s.id ? { ...x, title } : x)));
      setEditingId(null);
      toast.success("Renamed.");
    } catch { toast.error("Could not rename."); }
  };

  if (!user) {
    return (
      <aside data-testid="oracle-history" className="hairline p-5">
        <div className="label mb-3">Your conversations</div>
        <p className="serif italic text-sm text-[hsl(var(--oracle-foreground)/0.65)]">
          Sign in to keep a thread between you and the Oracle across visits.
        </p>
      </aside>
    );
  }

  return (
    <aside data-testid="oracle-history" className="hairline p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="label">Your conversations</div>
        <button
          data-testid="oracle-new-chat"
          onClick={onNewChat}
          className="reaction-pill"
          title="Start a new conversation"
        >
          <Plus size={12} strokeWidth={2} /> New
        </button>
      </div>

      {loading && <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Loading…</div>}

      {!loading && sessions.length === 0 && (
        <p className="serif italic text-sm text-[hsl(var(--oracle-foreground)/0.65)]">
          No past conversations yet. Ask your first question — it will be saved here.
        </p>
      )}

      <ul className="space-y-1 max-h-[60vh] overflow-y-auto -mx-2">
        {sessions.map((s) => {
          const active = s.id === currentSessionId;
          const isEditing = editingId === s.id;
          return (
            <li key={s.id}>
              <div
                role="button"
                tabIndex={0}
                data-testid={`oracle-session-${s.id}`}
                onClick={() => !isEditing && loadSession(s)}
                onKeyDown={(e) => { if (!isEditing && (e.key === "Enter" || e.key === " ")) loadSession(s); }}
                className={`group px-3 py-2.5 cursor-pointer transition-colors ${active ? "bg-[hsl(var(--accent)/0.18)]" : "hover:bg-secondary"}`}
              >
                <div className="flex items-start gap-2">
                  <Clock size={11} strokeWidth={1.5} className="mt-1.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        data-testid={`oracle-rename-input-${s.id}`}
                        autoFocus
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") saveRename(e, s);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        maxLength={80}
                        className="input-line w-full text-sm"
                      />
                    ) : (
                      <div className="serif text-[15px] text-foreground leading-snug line-clamp-2">
                        {s.title || "Untitled conversation"}
                      </div>
                    )}
                    <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1 flex items-center gap-2">
                      <span>{relativeTime(s.last_message_at || s.created_at)}</span>
                      {s.message_count > 0 && <span>· {s.message_count} msg</span>}
                    </div>
                  </div>
                  <div className={`flex flex-col gap-1 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
                    {isEditing ? (
                      <>
                        <button
                          data-testid={`oracle-rename-save-${s.id}`}
                          onClick={(e) => saveRename(e, s)}
                          className="text-[hsl(var(--primary))] hover:text-foreground"
                          title="Save"
                        >
                          <Check size={13} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                          className="text-muted-foreground hover:text-foreground"
                          title="Cancel"
                        >
                          <X size={13} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          data-testid={`oracle-rename-${s.id}`}
                          onClick={(e) => startRename(e, s)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Rename"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          data-testid={`oracle-delete-${s.id}`}
                          onClick={(e) => remove(e, s)}
                          className="text-muted-foreground hover:text-[hsl(var(--destructive,0_70%_45%))]"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
